import { BlockType } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { Icon } from 'src/common/components/icon';
import { IconUiSizeBox } from 'src/components/icon-ui-size-box';
import { useOpenNew } from 'src/hooks/block/use-open-new';
import { useCreateDocPage } from 'src/hooks/page';
import { useDropInfoNewPageShowLine } from 'src/services/app/hook/use-drop-info';
import { bizTracker } from 'src/utils/biz-tracker';
import { usePickBlock } from 'src/utils/pick-block';
import { bindDataTestId } from 'src/utils/qa-utils';
import { useTocGroupType } from './toc-group-header';
import { NEW_SUB_PAGE_NAMES } from './types';

interface Props {
  uuid: string;
  prevId?: string;
  deep: number;
}

export const NEW_PAGE_NAMES = {
  [BlockType.PAGE]: '创建新页面',
  [BlockType.FOLDER]: '创建文件夹',
};

/** 在底部添加新页面 */
export const NewPage: FC<Props> = ({ uuid, prevId, deep }) => {
  const createPage = useCreateDocPage();
  const parent = usePickBlock(uuid, []);
  const groupType = useTocGroupType();
  const openNew = useOpenNew();

  const IS_FIRST = deep === 0;

  const showLine = useDropInfoNewPageShowLine();

  if (!parent) return null;

  const type = IS_FIRST ? BlockType.PAGE : (parent.type as BlockType.PAGE | BlockType.FOLDER);

  const handleClick = async () => {
    bizTracker.event('subpage_new', { group_category: groupType });
    const id = createPage(type, { parentId: uuid, after: prevId }, groupType);
    openNew(id, IS_FIRST);
  };

  return (
    <div
      onClick={handleClick}
      className="relative flex items-center h-8 mx-2 rounded-md text-t2-medium animate-hover-black3 text-grey3"
      style={{ paddingLeft: deep * 20 }}
      {...bindDataTestId(NEW_PAGE_NAMES[type])}
    >
      {showLine && (
        <div className="absolute left-0 w-full -top-px h-0.5 bg-black pointer-events-none" />
      )}
      <IconUiSizeBox className="ml-2" size={20}>
        <Icon size="small" name="IcAdd" />
      </IconUiSizeBox>
      <span className="pl-1 text-ellipsis">
        {IS_FIRST ? NEW_PAGE_NAMES[type] : NEW_SUB_PAGE_NAMES[type]}
      </span>
    </div>
  );
};
